// ===========================================================================
// Chat text size.
//
// Chat is the one part of Lumi people read for minutes at a time, and the
// default is small -- 0.875rem. Anyone who finds that hard to read had no way
// to change it short of zooming the whole page, which wrecks a layout built
// around a video grid. So the message area scales on its own.
//
// It works by setting --chat-scale on the document, which the message styles
// multiply into their own font sizes. Emoji come along for free: in a message
// they ARE text, so they scale with it, and the picker's buttons are scaled by
// the same variable, since someone who cannot read the messages cannot pick an
// emoji out of a grid either.
//
// The choice is per-browser (localStorage) rather than per-account: it is a
// property of the screen you are reading on, not of who you are, and someone
// on a shared machine should not have their eyesight follow them around.
// ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';

const KEY = 'lumi.chatScale';

// Named steps rather than free zoom: four sizes people can actually tell apart,
// with a ceiling that still leaves the composer usable on a phone.
export const STEPS = [
  { scale: 1, label: 'Normal' },
  { scale: 1.15, label: 'Large' },
  { scale: 1.3, label: 'Larger' },
  { scale: 1.5, label: 'Largest' },
];

function clampIndex(i) {
  return Math.max(0, Math.min(STEPS.length - 1, i));
}

/** The stored step index, or 0 when there is nothing stored or storage is off. */
export function storedIndex() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw == null) return 0;
    const i = STEPS.findIndex((s) => String(s.scale) === String(raw));
    return i >= 0 ? i : 0;
  } catch {
    return 0; // private window, or storage blocked -- the default is fine
  }
}

/** Put the chosen scale on the document so the CSS can pick it up. */
export function applyTextSize(index = storedIndex()) {
  const step = STEPS[clampIndex(index)];
  try {
    document.documentElement.style.setProperty('--chat-scale', String(step.scale));
  } catch {
    /* nothing to do -- the stylesheet default stands */
  }
  return step;
}

function store(index) {
  try {
    localStorage.setItem(KEY, String(STEPS[clampIndex(index)].scale));
  } catch {
    /* not persisting is survivable; the size still applies for this session */
  }
}

/**
 * An A- / A+ pair for a chat header.
 *
 * Two buttons rather than a menu: the whole point is to be usable by someone
 * who is struggling to read the screen, and "make it bigger" should not require
 * finding a settings panel first. Each press announces the new size to a screen
 * reader, and the buttons disable at the ends so the control says plainly when
 * there is no more room to grow.
 */
export function createTextSizeControl() {
  let index = storedIndex();

  const live = el('span', { class: 'sr-only', aria: { live: 'polite' } });
  const smaller = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm text-size-btn',
    text: 'A-',
    title: t('Smaller chat text'),
    aria: { label: 'Make chat text smaller' },
    onClick: () => set(index - 1),
  });
  const bigger = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm text-size-btn text-size-btn-lg',
    text: 'A+',
    title: t('Larger chat text'),
    aria: { label: 'Make chat text larger' },
    onClick: () => set(index + 1),
  });

  function set(next) {
    const clamped = clampIndex(next);
    if (clamped === index && next !== index) return; // already at the end
    index = clamped;
    const step = applyTextSize(index);
    store(index);
    smaller.disabled = index === 0;
    bigger.disabled = index === STEPS.length - 1;
    live.textContent = `Chat text size: ${step.label}`;
  }

  applyTextSize(index);
  smaller.disabled = index === 0;
  bigger.disabled = index === STEPS.length - 1;

  return el('div', { class: 'text-size', title: t('Chat text size') }, [smaller, bigger, live]);
}

export default { createTextSizeControl, applyTextSize, storedIndex, STEPS };
